// competition-charts.js — Chart.js factories with 1980s phosphor-console aesthetic.
// All charts: green phosphor on black, monospace font, faint grid, glow on lines.

import { Chart, registerables } from 'chart.js';
import Annotation from 'chartjs-plugin-annotation';
Chart.register(...registerables, Annotation);

// ── Retro palette ────────────────────────────────────────────────────────────
export const RETRO = {
  green:    '#33ff33',
  greenDim: '#1a8c1a',
  amber:    '#ffb000',
  cyan:     '#00ffff',
  magenta:  '#ff33ff',
  white:    '#ccffcc',
  bg:       '#050a05',
  grid:     'rgba(51,255,51,0.10)',
  zero:     'rgba(51,255,51,0.35)',
  font:     "'Courier New', Courier, monospace",
};

// Season colors — one per season, cycling
const SEASON_COLORS = [RETRO.green, RETRO.amber, RETRO.cyan, RETRO.magenta, RETRO.white];
export function seasonColor(idx) { return SEASON_COLORS[idx % SEASON_COLORS.length]; }

// Caption group colors
export const CAPTION_COLORS = {
  music:          '#33ff33',
  visual:         '#00ffff',
  general_effect: '#ffb000',
  guard:          '#ff33ff',
  percussion:     '#ff6633',
};
export function captionColor(group) { return CAPTION_COLORS[group] || '#aaffaa'; }

// ── Base chart defaults ──────────────────────────────────────────────────────
function applyRetroDefaults() {
  Chart.defaults.color              = RETRO.greenDim;
  Chart.defaults.borderColor        = RETRO.grid;
  Chart.defaults.font.family        = RETRO.font;
  Chart.defaults.font.size          = 10;
  Chart.defaults.plugins.legend.labels.color = RETRO.green;
  Chart.defaults.plugins.legend.labels.font  = { family: RETRO.font, size: 10 };
  Chart.defaults.plugins.tooltip.backgroundColor = '#0a1a0a';
  Chart.defaults.plugins.tooltip.borderColor      = RETRO.greenDim;
  Chart.defaults.plugins.tooltip.borderWidth      = 1;
  Chart.defaults.plugins.tooltip.titleColor       = RETRO.green;
  Chart.defaults.plugins.tooltip.bodyColor        = RETRO.white;
  Chart.defaults.plugins.tooltip.titleFont        = { family: RETRO.font, size: 10 };
  Chart.defaults.plugins.tooltip.bodyFont         = { family: RETRO.font, size: 10 };
}
applyRetroDefaults();

const retroScales = {
  x: {
    ticks:  { color: RETRO.greenDim, font: { family: RETRO.font, size: 9 } },
    grid:   { color: RETRO.grid },
    border: { color: RETRO.greenDim },
  },
  y: {
    ticks:  { color: RETRO.greenDim, font: { family: RETRO.font, size: 9 } },
    grid:   { color: RETRO.grid },
    border: { color: RETRO.greenDim },
  }
};

// ── makeLineChart ────────────────────────────────────────────────────────────
// Base factory. Returns Chart instance.
export function makeLineChart(canvasEl, datasets, options = {}) {
  return new Chart(canvasEl, {
    type: 'line',
    data: { datasets },
    options: {
      responsive: true,
      maintainAspectRatio: false,
      interaction: { mode: 'index', intersect: false },
      scales:  { ...retroScales, ...options.scales },
      plugins: {
        legend: { display: options.legend !== false },
        tooltip: { enabled: true },
        annotation: { annotations: options.annotations || {} },
        ...options.plugins
      },
      animation: { duration: 400 },
      ...options
    }
  });
}

// ── makeSeasonArcChart ───────────────────────────────────────────────────────
// Multi-season total-score arc + optional per-caption lines + forecast dashed extension.
// seasons: array of season objects from /api/competition-analysis/season-arc/:bandId
// visibleCaptions: Set of caption group strings to render (empty = total only)
export function makeSeasonArcChart(canvasEl, seasons, visibleCaptions = new Set()) {
  const datasets = [];

  seasons.forEach((s, idx) => {
    const color  = seasonColor(idx);
    const points = s.entries.map(e => ({ x: e.show_number, y: e.total_score }));

    // Total score line
    datasets.push({
      label:           `${s.season} Total`,
      data:            points,
      borderColor:     color,
      backgroundColor: 'transparent',
      borderWidth:     2,
      pointRadius:     4,
      pointHoverRadius: 6,
      pointBackgroundColor: color,
      tension:         0.3,
      parsing: false,
    });

    // Caption sub-lines (dimmer, thinner)
    if (visibleCaptions.size) {
      for (const group of visibleCaptions) {
        const capPoints = s.entries
          .filter(e => e.captions[group] != null)
          .map(e => ({ x: e.show_number, y: e.captions[group] }));
        if (!capPoints.length) continue;
        datasets.push({
          label:           `${s.season} ${group}`,
          data:            capPoints,
          borderColor:     captionColor(group),
          backgroundColor: 'transparent',
          borderWidth:     1,
          borderDash:      [],
          pointRadius:     2,
          tension:         0.3,
          parsing: false,
        });
      }
    }

    // Forecast extension (dashed, most recent season only)
    if (idx === seasons.length - 1 && s.forecast && s.entries.length) {
      const last    = s.entries[s.entries.length - 1];
      const showNum = last.show_number;
      const f       = s.forecast;
      const projShow = showNum + (f.generated_after_show ? 0 : 1);

      // Dashed mid line
      datasets.push({
        label:           `${s.season} Forecast`,
        data:            [{ x: showNum, y: last.total_score }, { x: projShow + 3, y: parseFloat(f.projected_mid) }],
        borderColor:     color,
        backgroundColor: 'transparent',
        borderWidth:     1.5,
        borderDash:      [6, 4],
        pointRadius:     0,
        tension:         0,
        parsing:         false,
      });

      // Confidence band: projected_low filled to projected_high
      datasets.push({
        label:           `${s.season} Proj. High`,
        data:            [{ x: showNum, y: last.total_score }, { x: projShow + 3, y: parseFloat(f.projected_high) }],
        borderColor:     'transparent',
        backgroundColor: `${color}18`,
        fill:            '+1',
        borderWidth:     0,
        pointRadius:     0,
        parsing:         false,
      });
      datasets.push({
        label:           `${s.season} Proj. Low`,
        data:            [{ x: showNum, y: last.total_score }, { x: projShow + 3, y: parseFloat(f.projected_low) }],
        borderColor:     'transparent',
        backgroundColor: `${color}18`,
        fill:            false,
        borderWidth:     0,
        pointRadius:     0,
        parsing:         false,
      });
    }
  });

  const allX = seasons.flatMap(s => s.entries.map(e => e.show_number));
  const maxShow = allX.length ? Math.max(...allX) + 4 : 10;

  return makeLineChart(canvasEl, datasets, {
    scales: {
      ...retroScales,
      x: { ...retroScales.x, type: 'linear', min: 1, max: maxShow,
           title: { display: true, text: 'SHOW', color: RETRO.greenDim, font: { family: RETRO.font, size: 9 } } },
      y: { ...retroScales.y,
           title: { display: true, text: 'SCORE', color: RETRO.greenDim, font: { family: RETRO.font, size: 9 } } },
    },
    legend: true,
  });
}

// ── makeGapChart ─────────────────────────────────────────────────────────────
// Gap trend: primary_score - competitor_score at each shared event.
// positive gap = primary is AHEAD.
// Returns Chart instance; annotations added for "first time ahead" crossings.
export function makeGapChart(canvasEl, primary, competitors) {
  const datasets = [];
  const annotations = {};

  competitors.forEach((comp, idx) => {
    const color  = SEASON_COLORS[idx % SEASON_COLORS.length];
    const events = comp.shared_events || [];
    const points = events.map((e, i) => ({ x: i + 1, y: e.gap, label: e.competition_name }));

    datasets.push({
      label:           comp.band?.name || `Competitor ${idx + 1}`,
      data:            points,
      borderColor:     color,
      backgroundColor: 'transparent',
      borderWidth:     2,
      pointRadius:     5,
      pointHoverRadius: 7,
      pointBackgroundColor: points.map(p => p.y >= 0 ? color : `${color}66`),
      tension:         0.2,
      parsing:         false,
    });

    // "First time ahead" annotation — first positive gap after a negative one
    let wasNeg = events[0]?.gap < 0;
    events.forEach((e, i) => {
      if (wasNeg && e.gap >= 0) {
        annotations[`cross_${idx}_${i}`] = {
          type:        'line',
          xMin:        i + 1, xMax: i + 1,
          borderColor: color,
          borderWidth: 1,
          borderDash:  [4, 3],
          label: {
            display:         true,
            content:         '▲ LEAD',
            position:        'start',
            color:           color,
            font:            { family: RETRO.font, size: 8 },
            backgroundColor: 'transparent',
          }
        };
        wasNeg = false;
      }
      if (e.gap < 0) wasNeg = true;
    });
  });

  // Zero line annotation
  annotations.zero = {
    type:        'line',
    yMin: 0, yMax: 0,
    borderColor: RETRO.zero,
    borderWidth: 1.5,
    label: {
      display:         true,
      content:         '— EVEN —',
      position:        'center',
      color:           RETRO.greenDim,
      font:            { family: RETRO.font, size: 8 },
      backgroundColor: 'transparent',
    }
  };

  // X-axis labels from first competitor's events
  const firstComp = competitors[0];
  const labels = (firstComp?.shared_events || []).map((e, i) => {
    const date = e.event_date ? String(e.event_date).slice(0, 10) : '';
    return `${i + 1}`;
  });

  return makeLineChart(canvasEl, datasets, {
    scales: {
      ...retroScales,
      x: { ...retroScales.x, type: 'linear', min: 0.5,
           title: { display: true, text: 'SHARED SHOW', color: RETRO.greenDim, font: { family: RETRO.font, size: 9 } } },
      y: { ...retroScales.y,
           title: { display: true, text: 'GAP (+ = AHEAD)', color: RETRO.greenDim, font: { family: RETRO.font, size: 9 } } },
    },
    annotations,
    legend: true,
  });
}

// ── makeRadarChart ────────────────────────────────────────────────────────────
// Caption fingerprint radar — axes = caption groups, values = percentile rank 0–100.
// seasons: array of { season, percentiles: { group: pct } }
export function makeRadarChart(canvasEl, captionGroups, seasons) {
  const datasets = seasons.map((s, idx) => {
    const color = seasonColor(idx);
    return {
      label:           String(s.season),
      data:            captionGroups.map(g => s.percentiles[g] ?? 0),
      borderColor:     color,
      backgroundColor: `${color}15`,
      pointBackgroundColor: color,
      pointRadius:     4,
      borderWidth:     2,
    };
  });

  return new Chart(canvasEl, {
    type: 'radar',
    data: { labels: captionGroups.map(g => g.toUpperCase()), datasets },
    options: {
      responsive: true,
      maintainAspectRatio: false,
      scales: {
        r: {
          min: 0, max: 100,
          ticks: {
            stepSize: 25,
            color:    RETRO.greenDim,
            font:     { family: RETRO.font, size: 8 },
            backdropColor: 'transparent',
            callback: v => v + '%',
          },
          grid:        { color: RETRO.grid },
          angleLines:  { color: RETRO.grid },
          pointLabels: { color: RETRO.green, font: { family: RETRO.font, size: 9 } },
        }
      },
      plugins: {
        legend:  { labels: { color: RETRO.green, font: { family: RETRO.font, size: 10 } } },
        tooltip: {
          callbacks: {
            label: ctx => ` ${ctx.dataset.label}: ${ctx.raw.toFixed(1)}th percentile`
          }
        }
      },
      animation: { duration: 400 },
    }
  });
}

// ── Destroy helper ────────────────────────────────────────────────────────────
export function destroyChart(canvasEl) {
  const existing = Chart.getChart(canvasEl);
  if (existing) existing.destroy();
}
